import ResponsiveImage from "../../ui/images/ResponsiveImage";
import { useModal } from "../../../hooks/useModal"
import { Modal } from "../../ui/modal";
import { useCurrentLang } from "../../../pages/QrCodePages/QrCodePage"
import { 
  Hot, 
  Chili,
  Vegan,
  GlutenFree,
  CornFree,
  DairyFree,
  EggsFree,
  FishFree,
  LegumesFree,
  PeanutFree,
  SeedsFree,
  ShellfishFree,
  TreeNutsFree,
  SugarFreeOutline
} from '../../../icons'
import Label from "../../../components/form/Label";

import type { MenuItem, MenuItemAllergenKey } from "../../../types/menuItemTypes";
import type { Menu } from "../../../types/menuTypes";

type MenuItemCardProps = {
  menuItem: MenuItem;
  menu: Menu;
  currentLang?: string;
}

const hasAllergens = (menuItem: MenuItem) => {
   const allergens: MenuItemAllergenKey[] = [
    'sugarFree',
    'glutenFree',
    'cornFree',
    'dairyFree',
    'eggsFree',
    'fishFree',
    'legumesFree',
    'peanutFree',
    'seedsFree',
    'shellfishFree',
    'treeNutsFree'
   ]
   return allergens.some((a) => menuItem[a] === true)
}

const MenuItemQrCodeCard: React.FC<MenuItemCardProps> = ({
  menuItem,
  menu
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const currentLang = useCurrentLang((state: unknown) => (state as { currentLang?: string }).currentLang || '');
   
  const currentItem = menuItem?.menuItemLangs?.find((l) => l.language === currentLang) || menuItem.mainItemLang
  const image = menuItem.img.medium || '/images/icons/file-image-placeholder.svg'
  return (
    <div
      key={menuItem.id}
      className={`rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]`}
    >
      {/* Card Header */}
      <div className="p-4 flex flex-col h-full relative">
        <div className="absolute right-8 top-4">
        {menuItem.vegan && <Vegan className="self-center size-16 fill-[forestgreen]" />}
        </div>
        <ResponsiveImage onClick={openModal} imageUrl={image} />
        <h3 className="text-2xl mt-2 text-center font-bold text-gray-800 dark:text-white/90">
          {currentItem.name}
        </h3>
        {currentItem.description && (
          <p className="mt-1 text-md text-left text-gray-400 dark:text-gray-400">
            {currentItem.description}
          </p>
        )}
        <div className="mt-auto pt-4 flex flex-row items-center justify-between">
          <span className="self-center font-extrabold text-xl text-gray-900 dark:text-gray-100">{menuItem.price} {menu.currency.code}</span>
          <div className="flex flex-row gap-3">
            {menuItem.hot && <Hot className="self-center size-6 fill-[orangered]" />}
            {menuItem.chili && <Chili className="self-center size-6 fill-[indianred]" />}
            <span className="self-center text-md text-gray-400 dark:text-gray-400">{menuItem.mass}{menuItem.unit}</span>
          </div>

        </div>
      </div>

      {/* Card Body */}
      {/* <div className="p-4 border-t border-gray-200 text-gray-400 dark:border-gray-800 sm:p-6">
        <div className="space-y-6">body</div>
      </div> */}
      {/* Edit Modal */}
      <Modal 
        isOpen={isOpen}
        onClose={closeModal}
        showCloseButton
        className="max-w-[700px] max-h-[95vh] overflow-x-auto custom-scrollbar">
          <div className="p-4 flex flex-col h-full relative">
            <div className="absolute left-8 top-4">
              {menuItem.vegan && <Vegan className="self-center size-20 fill-[forestgreen]" />}
            </div>
            <ResponsiveImage onClick={openModal} imageUrl={image} />
            <h3 className="text-2xl mt-2 text-center font-bold text-gray-800 dark:text-white/90">
              {currentItem.name}
            </h3>
            {currentItem.description && (
              <p className="mt-1 text-md text-left text-gray-400 dark:text-gray-400">
                {currentItem.description}
              </p>
            )}
            <div className="mt-auto pt-4 flex flex-row items-center justify-between">
              <span className="self-center font-extrabold text-xl text-gray-900 dark:text-gray-100">{menuItem.price} {menu.currency.code}</span>
              <div className="flex flex-row gap-3">
                {menuItem.hot && <Hot className="self-center size-6 fill-[orangered]" />}
                {menuItem.chili && <Chili className="self-center size-6 fill-[indianred]" />}
                <span className="self-center text-md text-gray-400 dark:text-gray-400">{menuItem.mass}{menuItem.unit}</span>
              </div>
            </div>
            {hasAllergens(menuItem) && <div>
              <Label className='text-center my-4 text-gray-800 dark:text-white/90 self-center'>Allergens</Label>
              <div>
                <div className='grid grid-cols-3 md:grid-cols-5 gap-4'>
                  {menuItem.glutenFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Gluten free</Label>
                    <GlutenFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.cornFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Corn free</Label>
                    <CornFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.dairyFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Dairy free</Label>
                    <DairyFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.eggsFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Eggs free</Label>
                    <EggsFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.fishFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Fish free</Label>
                    <FishFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.legumesFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Legumes free</Label>
                    <LegumesFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.peanutFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Peanut free</Label>
                    <PeanutFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.seedsFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Seeds free</Label>
                    <SeedsFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.shellfishFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Shellfish free</Label>
                    <ShellfishFree className="self-center size-16 dark:fill-white" />
                  </div>}
                  {menuItem.treeNutsFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Tree nuts free</Label>
                    <TreeNutsFree className="self-center size-16 dark:fill-white" />  
                  </div>}
                  {menuItem.sugarFree && <div className='self-center w-28 flex flex-col justify-start border border-gray-200 rounded-2xl dark:border-gray-500 p-2'>
                    <Label className='text-center'>Sugar free</Label>
                    <SugarFreeOutline className="self-center size-16 dark:fill-white" />
                  </div>}
                </div>
              </div>
            </div>}
          </div>        
      </Modal>
    </div>
  );
};

export default MenuItemQrCodeCard;
